import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { ZodType } from 'zod';
import { useState } from 'react';
import { apiUrl } from '../../api/client';
import { ImageViewer } from '../../components/ImageViewer';
import viewerStyles from '../../components/ImageViewer.module.css';
import { formatDate } from '../salons/SalonSearchPage';
import { errorMessage } from '../../i18n/errors';
import { NotFoundPage } from '../system/NotFoundPage';
import styles from './Analytics.module.css';
import { TOPICS, useAnalytics, type Topic } from './queries';

/**
 * Prüft, ob ein Pfadsegment ein bekanntes Thema ist.
 * @param value Segment der Adresse.
 * @returns `true` bei bekanntem Thema.
 */
function isTopic(value: string | undefined): value is Topic {
  return value !== undefined && value in TOPICS;
}

/** Spalten einer Detailtabelle: Überschrift und Zelleninhalt je Zeile. */
type Column<Row> = { header: string; cell: (row: Row) => React.ReactNode };

/**
 * Liste eines Themas mit allen Zeilen (Detailseite zu Dashboard-Karten).
 * @param props Thema.
 */
function TopicTable({ topic }: { topic: Topic }) {
  const { t, i18n } = useTranslation();
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const query = useAnalytics(
    TOPICS[topic].path,
    TOPICS[topic].schema as unknown as ZodType<{ items: Array<Record<string, unknown>> }>,
  );
  if (query.isPending) return <p role="status">{t('common.loading')}</p>;
  if (query.isError) {
    return (
      <p role="alert" className={styles.error}>
        {errorMessage(t, query.error)}
      </p>
    );
  }
  const { items } = query.data;
  if (items.length === 0) return <p>{t('analytics.empty')}</p>;

  // Alle Zeilen mit Bild, in Listenreihenfolge: Grundlage der großen Bildansicht und ihres Blätterns.
  const imageItems = items
    .filter((row) => typeof row.imageId === 'number')
    .map((row) => ({ id: Number(row.imageId), title: String(row.title) }));
  /** Bild-Zelle mit Vorschau (Klick öffnet die große Ansicht) und Titel. */
  const imageCell = (row: Record<string, unknown>) => (
    <span className={styles.rowTitle}>
      <button
        type="button"
        className={viewerStyles.thumbButton}
        aria-label={t('viewer.open', { name: String(row.title) })}
        onClick={() =>
          setViewIndex(items.filter((r) => typeof r.imageId === 'number').indexOf(row))
        }
      >
        <img
          className={styles.thumb}
          src={apiUrl(`/images/${row.imageId}/thumb`)}
          alt=""
          loading="lazy"
        />
      </button>
      {String(row.title)}
    </span>
  );
  /** Zelle mit dem Textwert eines Feldes. */
  const number = (key: string) => (row: Record<string, unknown>) => String(row[key]);
  /** Zelle mit Erfolgsquote und Anteil. */
  const rate = (row: Record<string, unknown>) => `${row.rate} % (${row.accepted}/${row.decided})`;

  const columns: Record<Topic, Array<Column<Record<string, unknown>>>> = {
    'top-images': [
      { header: t('analytics.col.image'), cell: imageCell },
      { header: t('analytics.stat.acceptances'), cell: number('acceptances') },
      { header: t('analytics.stat.awards'), cell: number('awards') },
      { header: t('analytics.stat.countries'), cell: number('countries') },
    ],
    overlimit: [
      { header: t('analytics.col.image'), cell: imageCell },
      { header: t('analytics.col.listed'), cell: number('acceptances') },
      { header: t('analytics.col.raw'), cell: number('raw') },
      { header: t('analytics.stat.overflow'), cell: number('overflow') },
    ],
    potential: [
      { header: t('analytics.col.image'), cell: imageCell },
      { header: t('analytics.col.category'), cell: (row) => String(row.category ?? '–') },
      { header: t('analytics.stat.acceptances'), cell: number('acceptances') },
      { header: t('analytics.col.potential'), cell: number('potential') },
    ],
    flops: [
      { header: t('analytics.col.image'), cell: imageCell },
      { header: t('analytics.col.category'), cell: (row) => String(row.category ?? '–') },
      { header: t('analytics.col.submissions'), cell: number('submissions') },
    ],
    categories: [
      { header: t('analytics.col.category'), cell: number('key') },
      { header: t('analytics.col.rate'), cell: rate },
    ],
    countries: [
      { header: t('analytics.col.country'), cell: number('country') },
      { header: t('analytics.stat.acceptances'), cell: number('acceptances') },
    ],
    regions: [
      { header: t('analytics.col.region'), cell: number('key') },
      { header: t('analytics.col.rate'), cell: rate },
      {
        header: t('analytics.stat.countries'),
        cell: (row) => (row.countries as string[]).join(', '),
      },
    ],
    'best-per-region': [
      { header: t('analytics.col.region'), cell: number('region') },
      { header: t('analytics.col.image'), cell: imageCell },
      { header: t('analytics.stat.acceptances'), cell: number('acceptances') },
    ],
    'retry-suggestions': [
      { header: t('analytics.col.image'), cell: imageCell },
      {
        header: t('analytics.col.salon'),
        cell: (row) => {
          const salon = row.salon as { fiapNumber: string; name: string };
          return (
            <Link to={`/competitions/new?q=${encodeURIComponent(salon.fiapNumber)}`}>
              {`${salon.fiapNumber} ${salon.name}`}
            </Link>
          );
        },
      },
      {
        header: t('analytics.col.country'),
        cell: (row) => String((row.salon as { country: string | null }).country ?? '–'),
      },
      {
        header: t('analytics.col.nextClosing'),
        cell: (row) =>
          formatDate((row.salon as { closingDate: string }).closingDate, i18n.language),
      },
      {
        header: t('analytics.col.rate'),
        cell: (row) => `${row.rate} % (${row.accepted}/${row.decided})`,
      },
      { header: t('analytics.col.score'), cell: number('score') },
      {
        header: t('analytics.col.newCountry'),
        cell: (row) => (row.newCountry ? t('common.yes') : t('common.no')),
      },
    ],
    'country-opportunities': [
      { header: t('analytics.col.country'), cell: number('country') },
      { header: t('analytics.col.region'), cell: (row) => String(row.region ?? '–') },
      { header: t('analytics.col.openSalons'), cell: number('openSalons') },
      {
        header: t('analytics.col.nextClosing'),
        cell: (row) => formatDate(String(row.nextClosing), i18n.language),
      },
      {
        header: t('analytics.col.salon'),
        cell: (row) => {
          const salon = row.nextSalon as { name: string; fiapNumber: string };
          return `${salon.fiapNumber} ${salon.name}`;
        },
      },
    ],
    'country-gaps': [
      { header: t('analytics.col.country'), cell: number('country') },
      { header: t('analytics.col.region'), cell: (row) => String(row.region ?? '–') },
    ],
  };
  return (
    <>
      <p role="status">{t('analytics.rows', { count: items.length })}</p>
      <ImageViewer
        items={imageItems}
        index={viewIndex}
        onIndexChange={setViewIndex}
        onClose={() => setViewIndex(null)}
      />
      <table className={styles.table}>
        <thead>
          <tr>
            {columns[topic].map((column) => (
              <th key={column.header}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, index) => (
            <tr key={index}>
              {columns[topic].map((column) => (
                <td key={column.header}>{column.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** Detailseite einer Auswertung (`/analytics/:topic`). */
export function AnalyticsDetailPage() {
  const { t } = useTranslation();
  const { topic } = useParams();
  if (!isTopic(topic)) return <NotFoundPage />;
  return (
    <section>
      <p>
        <Link to="/">{t('analytics.back')}</Link>
      </p>
      <h1>{t(`analytics.topic.${topic}`)}</h1>
      <TopicTable topic={topic} />
    </section>
  );
}
